import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const TeacherProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/teacher/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setName(res.data.name || '');
      setEmail(res.data.email || '');
      setPhone(res.data.phone || '');
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await api.put('/teacher/profile', { name, email, phone }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfile(res.data.data);
      setEditing(false);
      alert('✅ Profil mis à jour !');
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('❌ Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      alert('❌ Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await api.post('/teacher/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Mot de passe modifié !');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  };

  if (loading) return <div className="p-6">Chargement...</div>;
  if (!profile) return <div className="p-6">Profil introuvable</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">👤 Mon profil</h1>
        <p className="text-gray-500">Gérez vos informations personnelles</p>
      </div>

      {/* Carte profil */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-4">
          <div className="w-20 h-20 rounded-full bg-[#E67E22] text-white flex items-center justify-center text-2xl font-bold">
            {getInitials(profile.name)}
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#2C3E50]">{profile.name}</h2>
            <p className="text-gray-500">{profile.email}</p>
            <span className="inline-block mt-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
              👨‍🏫 Professeur
            </span>
          </div>
        </div>
      </div>

      {/* Informations */}
      {!editing ? (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-[#2C3E50]">📋 Informations personnelles</h3>
            <button
              onClick={() => setEditing(true)}
              className="text-sm px-3 py-1 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400]"
            >
              ✏️ Modifier
            </button>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Nom complet</span>
              <span className="font-medium">{profile.name}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Email</span>
              <span className="font-medium">{profile.email}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Téléphone</span>
              <span className="font-medium">{profile.phone || '-'}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Rôle</span>
              <span className="font-medium">Professeur</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Établissement</span>
              <span className="font-medium">Lycée Dakar</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-bold text-[#2C3E50] mb-4">✏️ Modifier mes informations</h3>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nom complet *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Téléphone</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="+221 77 000 00 00"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : '💾 Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sécurité */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-[#2C3E50]">🔐 Sécurité</h3>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            {showPasswordForm ? 'Annuler' : '🔑 Changer le mot de passe'}
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mot de passe actuel *</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nouveau mot de passe *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirmer le mot de passe *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] disabled:opacity-50"
              >
                {saving ? 'Modification...' : '🔑 Changer le mot de passe'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TeacherProfile;